import { IconName } from "@fortawesome/pro-regular-svg-icons";

import { Optional } from "common/utils/types";

import { PatientKind } from ".";

/** Matches scheduler AppointmentStatus */
export const AppointmentStatus = {
  Creating: "CREATING",
  ClientCreating: "CLIENT_CREATING",
  Unscheduled: "UNSCHEDULED",
  Unconfirmed: "UNCONFIRMED",
  Confirmed: "CONFIRMED",
  DroppedOff: "DROPPED_OFF",
  CheckedIn: "CHECKED_IN",
  InProgress: "IN_PROGRESS",
  ReadyToGo: "READY_TO_GO",
  Completed: "COMPLETED",
  NoShow: "NO_SHOW",
  Canceled: "CANCELED",
  Imported: "IMPORTED",
  ClientRequested: "CLIENT_REQUESTED",
} as const;
export type AppointmentStatus = (typeof AppointmentStatus)[keyof typeof AppointmentStatus];

/**
 * Statuses that denote the patient is at the clinic.
 *
 * **Ordering here matters** - it follows the order they would
 * normally move through in the clinic.
 * */
export const IN_CLINIC_APPOINTMENT_STATUSES: AppointmentStatus[] = [
  AppointmentStatus.DroppedOff,
  AppointmentStatus.CheckedIn,
  AppointmentStatus.InProgress,
  AppointmentStatus.ReadyToGo,
];

/** Matches AppointmentKindSlugs */
export const AppointmentKindSlug = {
  ExamProgress: "EXAM_PROGRESS",
  ExamProgressExotic: "EXAM_PROGRESS_EXOTIC",
  ExamInjuryIllness: "EXAM_INJURY_ILLNESS",
  ExamInjuryIllnessExotic: "EXAM_INJURY_ILLNESS_EXOTIC",
  ExamUrgent: "EXAM_URGENT",
  ExamUrgentExotic: "EXAM_URGENT_EXOTIC",
  ExamWellness: "EXAM_WELLNESS",
  ExamWellnessExotic: "EXAM_WELLNESS_EXOTIC",
  ExamNewPatient: "EXAM_NEW_PATIENT",
  ProcedureSedated: "PROCEDURE_SEDATED",
  ProcedureHolistic: "PROCEDURE_HOLISTIC",
  ProcedureDiagnostics: "PROCEDURE_DIAGNOSTICS",
  SurgerySoftTissue: "SURGERY_SOFT_TISSUE",
  SurgerySpay: "SURGERY_SPAY",
  SurgeryNeuter: "SURGERY_NEUTER",
  SurgeryOrthoPedic: "SURGERY_ORTHOPEDIC",
  SurgeryOther: "SURGERY_OTHER",
  TelemedicineGeneral: "TELEMEDICINE_GENERAL",
  TelemedicineProgress: "TELEMEDICINE_PROGRESS",
  TechnicianVisit: "TECHNICIAN_VISIT",
  Dental: "DENTAL",
  Procedure: "PROCEDURE",
  Euthanasia: "EUTHANASIA",
  DeadOnArrival: "DEAD_ON_ARRIVAL",
  Grooming: "GROOMING",
  Custom: "CUSTOM",
  Boarding: "BOARDING",
  HouseCall: "HOUSE_CALL",
} as const;
export type AppointmentKindSlug = (typeof AppointmentKindSlug)[keyof typeof AppointmentKindSlug];

/** Matches CalendarResourceProviderSerializer */
type CalendarResourceProvider = {
  id: number;
  name: string;
  initials: string;
  image_url: string | null;
  user_id: number;
};

/** Matches scheduler CalendarResourceKind */
export const CalendarResourceKind = {
  Provider: "PROVIDER",
  Accommodation: "ACCOMMODATION",
} as const;
export type CalendarResourceKind = (typeof CalendarResourceKind)[keyof typeof CalendarResourceKind];

/** Matches AccommodationKindCalendarResourceSerializer */
export type AccommodationKindCalendarResource = {
  id: number;
  name: string;
  rank: number;
};

/** Matches AccommodationKindListSerializer */
export type AccommodationKind = {
  id: number;
  name: string;
  rank: number;
  allowed_species: PatientKind[];
  amenities: string[];
  min_weight: Optional<number>;
  max_weight: Optional<number>;
  calendar_resources: AccommodationKindCalendarResource[];
};

/** Matches GroupServiceForAccommodationKindSerializer */
export type GroupServiceForAccommodationKind = {
  id: number;
  display_name: string;
  code: string;
};

/** Matches AccommodationKindDetailSerializer */
export type AccommodationKindDetail = AccommodationKind & {
  services: GroupServiceForAccommodationKind[];
};

/** Matches CalendarResourceAccommodationKindSerializer */
export type CalendarResourceAccommodationKind = {
  id: number;
  name: string;
  rank: number;
  allowed_species: PatientKind[];
  amenities: string[];
  min_weight: Optional<number>;
  max_weight: Optional<number>;
};

/** Matches CalendarResourceSerializer */
export type CalendarResource = {
  id: number;
  name: string;
  provider: Optional<CalendarResourceProvider>;
  accommodation_kind: Optional<CalendarResourceAccommodationKind>;
  rank: number;
  can_double_book: boolean;
  can_client_double_book: boolean;
  icon: Optional<IconName>;
  is_imported: boolean;
  allowed_species: PatientKind[];
  kind: CalendarResourceKind;
};

/** matches scheduler TimeRangeData type */
type ResourceAvailabilityDateTime = {
  start_datetime: string;
  end_datetime: string;
  in_appropriate_designation: boolean;
};

/** Matches CalendarAvailabilityController.get_serialized_resource_data */
export type ResourceAvailabilityByResource = {
  resource: CalendarResource;
  availability_datetimes: ResourceAvailabilityDateTime[];
};

/** Matches CalendarAvailabilityController.get_serialized_resource_data_by_time */
export type ResourceAvailabilityByTime = {
  start_datetime: string;
  end_datetime: string;
  resources: CalendarResource[];
};
